import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { UserCog, Loader2, ShieldCheck } from "lucide-react";

/**
 * Modal disparado quando o admin muda um usuário para role "profissional".
 * Preenche em uma tacada: display_name, categorias, conselho, valor e bio.
 * Marca `status=approved` no backend — profissional já entra na régua de
 * indicação (só falta o selo pra aparecer na vitrine pública).
 */
const ProfessionalProfileModal = ({ user, onClose, onSaved }) => {
  const [f, setF] = useState({
    display_name: user?.name || "",
    categories: "",
    council_type: "CRM",
    council_number: "",
    council_state: "SP",
    session_duration: 50,
    session_value: 250,
    bio: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}/professional-profile`, {
        display_name: f.display_name,
        categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
        council_type: f.council_type,
        council_number: f.council_number,
        council_state: f.council_state,
        session_duration: Number(f.session_duration) || 50,
        session_value: Number(f.session_value) || 0,
        bio: f.bio,
      });
      toast.success("Perfil profissional migrado — agora selecione um selo para aparecer na vitrine");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prof-migrate-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-xl text-[#0B2B62] inline-flex items-center gap-2">
            <UserCog className="w-5 h-5 text-emerald-700" /> Preencher perfil profissional
          </h3>
          <p className="text-xs text-slate-500 mt-1 text-left">
            Migrando <b>{user?.name}</b> ({user?.email}) para <b>Profissional</b>. Preencha os dados básicos — o perfil nasce <b>aprovado</b>. Depois, selar (Ouro/Prata/Bronze) libera na vitrine pública.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome de exibição">
            <input value={f.display_name} onChange={(e) => set("display_name", e.target.value)}
              data-testid="pm-display-name"
              placeholder="Ex.: Dra. Nai Silva"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Categorias (separadas por vírgula)">
            <input value={f.categories} onChange={(e) => set("categories", e.target.value)}
              data-testid="pm-categories"
              placeholder="Psiquiatra, Psicólogo(a), Terapeuta"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Conselho">
            <select value={f.council_type} onChange={(e) => set("council_type", e.target.value)}
              data-testid="pm-council-type"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              {["CRM","CRP","CFN","CRN","CRT","CBO","COREN","CREF","CRESS","CREFITO","CRF","CREFONO","OUTRO"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Nº conselho / UF">
            <div className="flex gap-2">
              <input value={f.council_number} onChange={(e) => set("council_number", e.target.value)}
                data-testid="pm-council-num" placeholder="123456"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <input value={f.council_state} onChange={(e) => set("council_state", e.target.value.toUpperCase().slice(0,2))}
                data-testid="pm-council-state" placeholder="SP"
                className="w-16 px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
          </Field>
          <Field label="Duração da consulta (min)">
            <input type="number" value={f.session_duration} onChange={(e) => set("session_duration", e.target.value)}
              data-testid="pm-duration"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Valor por consulta (R$)">
            <input type="number" step="0.01" value={f.session_value} onChange={(e) => set("session_value", e.target.value)}
              data-testid="pm-value"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
        </div>

        <Field label="Bio (opcional)">
          <textarea rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)}
            data-testid="pm-bio"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </Field>

        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-[11px] text-emerald-800 flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Ao salvar, o cadastro é aprovado e o profissional aceita os termos v1-admin-migrated automaticamente (o admin tá vouchando). Ele/ela poderá completar bio, foto e agenda entrando em <b>/profissional</b>.</span>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="pm-save"
            className="text-sm text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Salvar perfil profissional
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default ProfessionalProfileModal;
